import React, { useCallback, useRef } from 'react';

import { IconProps, InputProps } from '@rbilabs/universal-components';

import AutocompleteOptionsDropdown from 'components/ucl/autocomplete-options-dropdown';
import type { AutocompleteSelectionPayload } from 'components/ucl/autocomplete-options-dropdown/types';
import type { PlacesSessionToken } from 'hooks/geolocation/types';
import useLocationPredictions from 'hooks/geolocation/use-location-predictions';
import useLocationPredictionsDelivery from 'hooks/geolocation/use-location-predictions-delivery';
import { CustomEventNames, EventTypes, logEvent } from 'state/amplitude';
import { t } from 'state/intl/translations';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { IPlaceData } from 'utils/geolocation';
import { asyncNoop } from 'utils/noop';

type BaseInputProps = Pick<InputProps, 'value'>;

export interface IAutocompleteAddressInputProps extends BaseInputProps {
  testID: string;
  autoFocus?: boolean;
  label: string;
  labelColor?: string;
  addressesOnly?: boolean;
  useDeliveryPredictions?: boolean;
  required: boolean;

  message?: string;
  setMessage?: React.Dispatch<React.SetStateAction<string>>;

  onAddressSelected: (
    address: string,
    placeId?: string,
    placeData?: IPlaceData,
    sessionToken?: PlacesSessionToken
  ) => Promise<void>;
  onInputValueChange: React.Dispatch<React.SetStateAction<string | undefined>>;
  onBlur?(): void;
  inputCustomProps?: InputProps;
  rightIconCustomProps?: IconProps;
}

export const AutocompleteAddressInput = React.forwardRef<
  HTMLInputElement,
  IAutocompleteAddressInputProps
>(
  (
    {
      testID,
      label,
      labelColor,
      message,
      addressesOnly = false,
      useDeliveryPredictions = false,
      onAddressSelected = asyncNoop,
      onInputValueChange,
      onBlur,
      setMessage,
      value = '',
      inputCustomProps,
      rightIconCustomProps,
    },
    ref
  ) => {
    /**
     * Retrieve predictions from the desired source and only pass the first five results.
     */
    const enableDoordashSuggestions =
      useFlag(LaunchDarklyFlag.ENABLE_DOORDASH_DRIVE_SUGGESTIONS) && useDeliveryPredictions;

    /**
     * Prevents an extra useLocationPredictions call when the user selects an address
     * from the dropdown. On selection, the parent updates the value with the full
     * address, which would trigger a new prediction fetch. This ref is set to true
     * on selection and false on input change, temporarily disabling predictions
     * until the user edits again.
     */
    const skipPredictionsRef = useRef(false);

    const deliveryPredictions = useLocationPredictionsDelivery(value, {
      enabled: enableDoordashSuggestions && !skipPredictionsRef.current,
    });

    const [defaultPredictions, sessionHelpers] = useLocationPredictions(value, {
      addressesOnly,
      enabled: !enableDoordashSuggestions && !skipPredictionsRef.current,
    });

    const predictions = (enableDoordashSuggestions
      ? deliveryPredictions
      : defaultPredictions
    )?.slice(0, 5);

    const handleDropdownValueSelected = useCallback(
      async ({ address, placeId, placeData }: AutocompleteSelectionPayload) => {
        skipPredictionsRef.current = true;
        const sessionToken = sessionHelpers.getSessionToken();
        await onAddressSelected(address, placeId, placeData, sessionToken);

        if (sessionToken) {
          sessionHelpers.resetSessionToken();
        }
        logEvent(CustomEventNames.MANUAL_LOCATION_SEARCH, EventTypes.Search);
      },
      [onAddressSelected, sessionHelpers]
    );

    const handleInputValueChange = useCallback(
      (inputValue: string | undefined) => {
        skipPredictionsRef.current = false;
        onInputValueChange(inputValue || '');

        if (setMessage) {
          setMessage('');
        }
      },
      [onInputValueChange, setMessage]
    );

    return (
      <AutocompleteOptionsDropdown
        noPredictionsFoundText={t('noLocationsMatchYourSearch')}
        testID={testID}
        onSelection={handleDropdownValueSelected}
        predictions={predictions}
        onInputChange={handleInputValueChange}
        onBlur={onBlur}
        value={value}
        label={label}
        labelColor={labelColor}
        errorMessage={message}
        ref={ref}
        clearTextIconTitle={t('clearText')}
        inputCustomProps={inputCustomProps}
        rightIconCustomProps={rightIconCustomProps}
      />
    );
  }
);

export default AutocompleteAddressInput;
